import {
  Document,
  Text,
  Page,
  StyleSheet,
  Image,
  View,
  Font,
  Link,
} from "@react-pdf/renderer";
import TopLogo from "../../assets/images/logo2.png";
import { BANDS, moduleBand } from "../../data/selfAppendixMapping";
import { learningUrl } from "../../data/learningLinks";

Font.register({
  family: "Tahoma",
  fonts: [
    { src: "/fonts/Tahoma-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/TAHOMAB0.TTF", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: { paddingTop: 36, paddingBottom: 48, paddingHorizontal: 50, fontFamily: "Tahoma", fontSize: 9.5, color: "#33475b" },
  coverLogo: { width: 150, alignSelf: "center", marginTop: 120, marginBottom: 30 },
  coverTitle: { fontSize: 24, fontWeight: "bold", color: "#0F5385", textAlign: "center", marginHorizontal: 30 },
  coverSub: { fontSize: 11, color: "#69AF45", textAlign: "center", marginTop: 16 },
  moduleTitle: { fontSize: 14, fontWeight: "bold", color: "#0F5385", marginBottom: 6 },
  intro: { fontSize: 9, color: "#5a6b7b", marginBottom: 10, lineHeight: 1.4 },
  bandHeaderRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 3, paddingHorizontal: 6 },
  bandHeader: { color: "#fff", fontSize: 9.5, fontWeight: "bold" },
  yourScoreTag: { color: "#fff", fontSize: 8, fontWeight: "bold", borderWidth: 1, borderColor: "#fff", borderRadius: 2, paddingHorizontal: 4, paddingVertical: 1 },
  bandBody: { borderWidth: 1, borderColor: "#e0e6eb", borderTopWidth: 0, padding: 6, marginBottom: 8 },
  callout: { flexDirection: "row", alignItems: "center", borderWidth: 1, borderRadius: 2, paddingHorizontal: 6, paddingVertical: 3, marginBottom: 8 },
  calloutText: { fontSize: 9 },
  recHeading: { fontWeight: "bold", color: "#0F5385" },
  rec: { marginBottom: 5, lineHeight: 1.4 },
  learnBox: { backgroundColor: "#f1f7fb", borderLeftWidth: 3, borderLeftColor: "#0F5385", padding: 8, marginBottom: 6 },
  learnTitle: { fontSize: 10, fontWeight: "bold", color: "#0F5385", marginBottom: 4 },
  learnItem: { fontSize: 9, marginBottom: 2 },
  learnLink: { color: "#0F5385", textDecoration: "underline" },
  pageNumber: { position: "absolute", bottom: 20, right: 50, fontSize: 8, color: "#9aa7b3" },
  footerLabel: { position: "absolute", bottom: 20, left: 50, fontSize: 8, color: "#9aa7b3" },
});

const BandBlock = ({ band, recs, isHit, dim }) => (
  <View wrap={false} style={{ opacity: dim ? 0.55 : 1 }}>
    <View style={[styles.bandHeaderRow, { backgroundColor: band.color }]}>
      <Text style={styles.bandHeader}>{band.label} ({band.range})</Text>
      {isHit && <Text style={styles.yourScoreTag}>★ YOUR SCORE</Text>}
    </View>
    <View style={[styles.bandBody, isHit ? { borderColor: band.color, borderWidth: 1.5, borderTopWidth: 0 } : {}]}>
      {(recs || []).map((r, i) => (
        <Text key={i} style={styles.rec}>
          {r.heading ? <Text style={styles.recHeading}>{r.heading}: </Text> : null}
          {r.body}
        </Text>
      ))}
    </View>
  </View>
);

const AppendixSelfPDF = ({ data, name, sectionsData } = {}) => (
  <Document>
    {/* Cover */}
    <Page size="A4" style={styles.page}>
      <Image src={TopLogo} style={styles.coverLogo} />
      <Text style={styles.coverTitle}>{data?.title}</Text>
      {name && <Text style={styles.coverSub}>{name}</Text>}
      <Text style={[styles.intro, { textAlign: "center", marginTop: 24, marginHorizontal: 40 }]}>
        For each module, your current level is highlighted based on your assessment score.
      </Text>
      <Text style={styles.pageNumber} render={({ pageNumber }) => `${pageNumber}`} fixed />
    </Page>

    {/* One page (or more) per module */}
    {(data?.modules || []).map((m, i) => {
      const band = moduleBand(sectionsData, m, i);
      const def = band && BANDS.find((b) => b.key === band.bandKey);
      return (
        <Page key={i} size="A4" style={styles.page}>
          <Text style={styles.moduleTitle}>{m.module}</Text>
          {def && (
            <View style={[styles.callout, { borderColor: def.color, backgroundColor: `${def.color}1A` }]}>
              <Text style={styles.calloutText}>
                Your score: <Text style={{ color: def.color, fontWeight: "bold" }}>{band.score}% — {def.label}</Text>
              </Text>
            </View>
          )}
          <Text style={styles.intro}>{m.intro || data?.intro}</Text>
          {BANDS.map((b) => (
            <BandBlock
              key={b.key}
              band={b}
              recs={m.bands?.[b.key]}
              isHit={band?.bandKey === b.key}
              dim={band?.bandKey && band.bandKey !== b.key}
            />
          ))}
          <View style={styles.learnBox} wrap={false}>
            <Text style={styles.learnTitle}>Know2Grow Recommended Formal Learning Opportunities</Text>
            {(m.forAll || []).map((it, j) => {
              const url = learningUrl(it);
              return (
                <Text key={j} style={styles.learnItem}>
                  • {url ? <Link src={url} style={styles.learnLink}>{it}</Link> : it}
                </Text>
              );
            })}
          </View>
          <Text style={styles.footerLabel} fixed>Appendix — Professional Development Recommendations</Text>
          <Text style={styles.pageNumber} render={({ pageNumber }) => `${pageNumber}`} fixed />
        </Page>
      );
    })}
  </Document>
);

export default AppendixSelfPDF;
